<template>
  <form class="max-w-5xl" @submit.prevent="emit('save')">
    <div class="mt-10 mb-40 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
      <form-input v-model.trim="program.name" label="Name" class="md:col-span-4" :error-msg="result.name.$messages.join(' | ')" />

      <form-input v-model.trim="program.processName" label="Process name (comma separated)" class="md:col-span-4" :error-msg="result.processName.$messages.join(' | ')" />

      <form-input
        v-model="program.automationAppId"
        label="Automation App Id"
        type="number"
        :range="[0, 9999]"
        class="md:col-span-4"
        :error-msg="result.automationAppId.$messages.join(' | ')"
      />

      <form-input v-model="program.description" label="Description" class="md:col-span-4" :error-msg="result.description.$messages.join(' | ')" />

      <form-textarea v-model="program.info" label="Info" class="col-span-full" />

      <form-input v-model="program.color" label="Card color" class="md:col-span-4" :error-msg="result.color.$messages.join(' | ')">
        <template #label-color>
          <div class="size-2 rounded-full" :style="{ backgroundColor: program.color }" />
        </template>
      </form-input>

      <form-input v-model="program.version" label="Version" class="md:col-span-4" :error-msg="result.version.$messages.join(' | ')" />

      <form-tags v-model="programExtensions" label="Extensions (Open With)" class="md:col-span-full" />

      <form-textarea v-model="program.fileTypes" label="Supported file types" class="col-span-full" />

      <form-textarea v-model="program.usageInfo" label="Used by professionals" class="col-span-full" />

      <form-radio-group
        v-model="program.licenseType"
        label="License type"
        :items="[
          { name: 'Free', value: 'free' },
          { name: 'Paid', value: 'paid' }
        ]"
        :error-msg="result.licenseType.$messages.join(' | ')"
        class="col-span-full"
      />

      <form-radio-group
        v-model="program.programType"
        label="Program type"
        :items="[
          { name: 'Default', value: 'default' },
          { name: 'Browser', value: 'browser' }
        ]"
        :error-msg="result.programType.$messages.join(' | ')"
        class="col-span-full"
      />

      <form-select
        v-model="program.developerId"
        label="Developer"
        placeholder="Select a developer"
        :items="stats?.developers"
        :error-msg="result.developerId.$messages.join(' | ')"
        class="col-span-4"
      >
        <template #icon>
          <PlusIcon class="size-5 ml-1 cursor-pointer" @click="openDialog('developer')" />
        </template>
      </form-select>

      <form-select
        v-model="program.publisherId"
        label="Publisher"
        placeholder="Select a publisher"
        :items="stats?.publishers"
        :error-msg="result.publisherId.$messages.join(' | ')"
        class="col-span-4"
      >
        <template #icon>
          <PlusIcon class="size-5 ml-1 cursor-pointer" @click="openDialog('publisher')" />
        </template>
      </form-select>

      <form-select
        v-model="program.categoryId"
        label="Category"
        placeholder="Select a category"
        :items="stats?.categories"
        :error-msg="result.categoryId.$messages.join(' | ')"
        class="col-span-4"
      >
        <template #icon>
          <PlusIcon class="size-5 ml-1 cursor-pointer" @click="openDialog('category')" />
        </template>
      </form-select>

      <div class="col-span-2" />

      <form-image-upload v-model="program.iconUrl" label="App Icon" class="col-span-2" :error-msg="result.iconUrl.$messages.join(' | ')" />

      <form-image-upload v-model="program.imageUrl" label="Main image" class="col-span-2" :error-msg="result.imageUrl.$messages.join(' | ')" />

      <form-image-upload v-model="program.splashImageUrl" label="Splash image" class="col-span-2" :error-msg="result.splashImageUrl.$messages.join(' | ')" />

      <form-multiple-images-upload v-model="program.imageUrls" label="Images" class="col-span-full" :error-msg="result.imageUrls.$messages.join(' | ')" @main-image="handleMainImge" />

      <program-plugins
        v-model="program.Plugins"
        class="col-span-full mt-10"
        :disabled="program.deletedAt || program.isEnabled === false"
      />
    </div>
  </form>
</template>

<script setup>
import { computed, reactive } from 'vue';
import FormInput from './FormInput.vue';
import FormTextarea from './FormTextarea.vue';
import FormRadioGroup from './FormRadioGroup.vue';
import FormSelect from './FormSelect.vue';
import FormTags from './FormTags.vue';
import FormImageUpload from './FormImageUpload.vue';
import FormMultipleImagesUpload from './FormMultipleImagesUpload.vue';
import ModalPrompt from './ModalPrompt.vue';
import { PlusIcon } from '@heroicons/vue/24/outline';
import { getStats, reloadStats } from '../composables/useProgramStats';
import { Modal } from '../composables/useModal';
import useValidate from 'vue-tiny-validate';
import ProgramPlugins from './ProgramPlugins.vue';

const propNameByEntity = {
  developer: 'developerId',
  publisher: 'publisherId',
  category: 'categoryId'
};

const program = defineModel('program', {
  type: Object,
  default: () => ({
    name: '',
    processName: null,
    automationAppId: null,
    description: '',
    color: '',
    version: '',
    info: '',
    licenseType: '',
    programType: '',
    developerId: null,
    publisherId: null,
    categoryId: null,
    iconUrl: '',
    imageUrl: '',
    splashImageUrl: '',
    imageUrls: [],
    Extensions: []
  })
});

const programExtensions = computed({
  get() {
    return program.value.Extensions?.map(ext => ext.name) || [];
  },
  set(value) {
    const result = [];
    for (let name of value) {
      name = name.toLowerCase().trim().replaceAll('.', '');
      const existing = program.value.Extensions.find(ext => ext.name === name);
      if (existing) {
        result.push(existing);
      } else if (name && !result.includes(name)) {
        result.push({ name });
      }
    }
    program.value.Extensions = result;
  }
});

const emit = defineEmits([ 'save' ]);

defineExpose({
  onValidate,
  getResult
});

const rules = reactive({
  name: {
    name: 'required',
    test: value => Boolean(value),
    message: 'Name must not be empty.'
  },
  processName: {
    name: 'required',
    test: () => true,
    message: ''
  },
  automationAppId: {
    name: 'required',
    test: value => Boolean(value),
    message: 'Automation App Id must not be empty.'
  },
  description: {
    name: 'required',
    test: () => true, // value => Boolean(value),
    message: 'Description must not be empty.'
  },
  color: {
    name: 'validHexColor',
    test: value => {
      if (!value) return true;
      return /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(value);
    },
    message: 'Color must be a valid HEX code (e.g., #121212 or #FFF).'
  },
  version: {
    name: 'required',
    test: () => true,
    message: 'Version must not be empty.'
  },
  licenseType: {
    name: 'required',
    test: value => Boolean(value),
    message: 'LicenseType must not be empty.'
  },
  programType: {
    name: 'required',
    test: value => Boolean(value),
    message: 'ProgramType must not be empty.'
  },
  developerId: {
    name: 'required',
    test: value => Boolean(value),
    message: 'Developer must not be empty.'
  },
  publisherId: {
    name: 'required',
    test: value => Boolean(value),
    message: 'Publisher must not be empty.'
  },
  categoryId: {
    name: 'required',
    test: value => Boolean(value),
    message: 'Category must not be empty.'
  },
  iconUrl: {
    name: 'required',
    test: value => Boolean(value),
    message: 'App icon must not be empty.'
  },
  imageUrl: {
    name: 'required',
    test: value => Boolean(value),
    message: 'Main image must not be empty.'
  },
  splashImageUrl: {
    name: 'required',
    test: value => Boolean(value),
    message: 'Splash image must not be empty.'
  },
  imageUrls: [
    { name: 'required', test: value => Boolean(value) },
    {
      name: 'required',
      test: value => value,
      message: 'There must be a minimum of 1 image.'
    }
  ]
});
const options = reactive({
  autoTest: true
});
const { result } = useValidate(program, rules, options);

async function onValidate() {
  return await result.value.$test();
}

function getResult() {
  return result.value;
}

const stats = getStats();

async function openDialog(entity) {
  const newEntity = await Modal.dialog({
    title: `Add new ${entity}`,
    component: ModalPrompt,
    componentData: { label: 'Name', placeholder: 'Type name', iconColor: 'primary', entity }
  });

  if (!newEntity) {
    return;
  }

  const propName = propNameByEntity[entity];

  if (!propName) {
    console.log('Invalid entity name');
    return;
  }

  await reloadStats();

  program.value[propName] = newEntity;
}

function handleMainImge(newImg) {
  program.value.imageUrl = newImg;
}
</script>
